import { Table } from 'react-dsgov';
import { IAquisicao, IAquisicaoListagem } from '../../../../../../../interfaces/IAquisicao';
import { BrButton, BrLoading } from '@govbr-ds/react-components';
import styles from './aquisicaoTable.module.scss';
import { formatCurrency } from '../../../../../../../utils/formatCurrency';
import { useContext } from 'react';
import { DiefCfemContext } from '../../../../../../../contexts/DiefCfemContext';
import { formatarNrProcesso } from '../../../../../../../utils/formatNrProcesso';

interface IHeader {
  field: string;
  label: string;
}

interface IProps {
  data: IAquisicaoListagem[];
  headers: IHeader[];
  isLoading: boolean;
  mapAquisicoesToListagem: () => void;
  setEdicaoAquisicao: React.Dispatch<React.SetStateAction<IAquisicao | undefined>>;
  setNovaAquisicao: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AquisicaoTable = ({
  data,
  headers,
  isLoading,
  mapAquisicoesToListagem,
  setEdicaoAquisicao,
  setNovaAquisicao,
}: IProps) => {
  const context = useContext(DiefCfemContext);
  const declaracaoPrimeiroAdquirente = context?.declaracaoPrimeiroAdquirente;

  const excluirAquisicao = (aquisicao: IAquisicao) => {
    //TODO - colocar modal perguntando se quer excluir mesmo

    if (!aquisicao?.municipio?.codigoIBGE || !aquisicao?.numeroProcesso || !aquisicao?.substancia) {
      return;
    }

    if (declaracaoPrimeiroAdquirente) {
      declaracaoPrimeiroAdquirente.aquisicoes = declaracaoPrimeiroAdquirente?.aquisicoes?.filter(
        (obj) =>
          !(
            obj.municipio.codigoIBGE === aquisicao.municipio.codigoIBGE &&
            obj.numeroProcesso === aquisicao.numeroProcesso &&
            obj.substancia === aquisicao.substancia
          )
      );
      mapAquisicoesToListagem();
    }
  };

  const editarAquisicao = (aquisicao: IAquisicao) => {
    setEdicaoAquisicao(aquisicao);
    window.scrollTo(0, 0);
    setNovaAquisicao(true);
  };

  return (
    <div className={styles.customTable}>
      {data.length === 0 && !isLoading ? (
        <Table
          title={'Processo Minerário - Nº (-) | Título - Lavra Garimpeira'}
          showTopBar={true}
          headers={headers}
          showPagination={false}
          showDensityButtons={false}
          showSearch={false}
          density='medium'
        >
          <tbody className={styles.noRecords}>
            <tr>
              <td colSpan={headers.length}>
                <div className={styles.noRecords}>
                  {' '}
                  Não há aquisições para serem exibidas. <br></br>
                  Por favor adicione uma aquisição.
                </div>
              </td>
            </tr>
          </tbody>
        </Table>
      ) : (
        data.map((row, index) => (
          <Table
            title={
              'Processo Minerário - Nº ' +
              (formatarNrProcesso(row.numeroProcesso) || '-') +
              ' | ' +
              (row.titulo || 'Título - Lavra Garimpeira')
            }
            showTopBar={true}
            headers={headers}
            showPagination={false}
            showDensityButtons={false}
            showSearch={false}
            density='medium'
            className={styles.aquisicaoTable}
            key={index}
          >
            <tbody>
              {row.aquisicoes.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan={headers.length}>
                    <div className={styles.noRecords}>
                      Não há aquisições para serem exibidas. Por favor adicione uma aquisição.
                    </div>
                  </td>
                </tr>
              ) : (
                Array.isArray(row.aquisicoes) &&
                row.aquisicoes.map((rowAquisicao, index) => (
                  <tr key={index}>
                    {headers.map((header) => {
                      if (header?.label === 'Operações')
                        return (
                          <>
                            <td align='center' key={header.field}>
                              <div className={styles.statusColumn}>
                                <BrButton
                                  secondary
                                  circle
                                  onClick={() => editarAquisicao(rowAquisicao.aquisicao)}
                                  title='Editar'
                                >
                                  <i className={`fas fa-edit fa-md  ${styles.columnIcon}`} />
                                </BrButton>
                                <BrButton
                                  secondary
                                  circle
                                  onClick={() => excluirAquisicao(rowAquisicao.aquisicao)}
                                  title='Excluir'
                                >
                                  <i className={`fas fa-trash-alt fa-md  ${styles.columnIcon}`} />
                                </BrButton>
                              </div>
                            </td>
                          </>
                        );
                      else if (header?.label === 'Preço Unitário') {
                        const valorAquisicao = parseFloat(rowAquisicao.valorAquisicao || '');
                        const quantidade = parseFloat(rowAquisicao.quantidade?.toString() || '');

                        const precoUnitario =
                          !isNaN(valorAquisicao) && !isNaN(quantidade) && quantidade > 0 && valorAquisicao > 0
                            ? valorAquisicao / quantidade
                            : null;

                        return (
                          <td align='center' key={header.field}>
                            {precoUnitario !== null ? formatCurrency(precoUnitario) : '-'}
                          </td>
                        );
                      } else if (header?.label === 'Valor de Aquisição') {
                        const valorAquisicao = rowAquisicao[header.field];
                        return (
                          <td align='center' key={header.field}>
                            {valorAquisicao ? formatCurrency(valorAquisicao) : '-'}
                          </td>
                        );
                      } else if (header?.label === 'Deduções') {
                        const totalDeducoes =
                          rowAquisicao.aquisicao?.componentesBaseDeCalculo?.reduce(
                            (accBase, baseCalculo) =>
                              accBase +
                              baseCalculo.codigosNCM.reduce(
                                (accNcm, codigoNCM) =>
                                  accNcm +
                                  (codigoNCM.tributosValores?.reduce(
                                    (accTributo, tributo) => accTributo + (Number(tributo?.valor) || 0),
                                    0
                                  ) ?? 0),
                                0
                              ),
                            0
                          ) ?? 0;
                        return (
                          <td align='center' key={header.field}>
                            {formatCurrency(totalDeducoes.toFixed(2))}
                          </td>
                        );
                      } else if (header?.label === 'Quantidade') {
                        const valorAquisicao = rowAquisicao[header.field];
                        return (
                          <td align='center' key={header.field}>
                            {valorAquisicao !== undefined && valorAquisicao !== null
                              ? valorAquisicao.toString().replace('.', ',')
                              : '-'}
                          </td>
                        );
                      } else if (header?.label === 'Município/UF') {
                        return (
                          <td align='center' key={header.field}>
                            <div>
                              {rowAquisicao?.aquisicao.municipio
                                ? `${rowAquisicao.aquisicao.municipio.nome}/${rowAquisicao.aquisicao.municipio.uf}`
                                : ''}
                            </div>
                          </td>
                        );
                      } else if (
                        header?.label === 'Substância' ||
                        header?.label === 'Unidade' ||
                        header?.label === 'Tipo de Uso'
                      )
                        return (
                          <td align='center' key={header.field}>
                            {rowAquisicao.aquisicao[header.field] || '-'}
                          </td>
                        );
                      else
                        return (
                          <td align='center' key={header.field}>
                            {rowAquisicao[header.field] || '-'}
                          </td>
                        );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        ))
      )}
      {isLoading && (
        <div className={styles.tableLoading}>
          <BrLoading large label='Carregando...' />
        </div>
      )}
    </div>
  );
};
